import { useEffect, useState } from "react";
import {
    Sheet,
    SheetClose,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { X } from "lucide-react";
import { FilePenLine } from "lucide-react"; // Add this import
// import { Route as singleItemInventory } from '@/routes/(pharma)/inventory/item/$type.$itemId'
import { NonMedicalInventoryPriceHistory } from "../-data/model";
import { useRouter } from "@tanstack/react-router";
import { Separator } from "@/components/ui/separator";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import { convertToCurrencyRounded } from "@/utils/currency_converter";
import { DiscountType, ItemType } from "@/gen/inventory/common_pb";
import { pharmacyClient } from "@/connect";
import { Skeleton } from "@/components/ui/skeleton";

const EditItem = ({ itemId, type, disabled, level }: { itemId: string; type: ItemType; disabled: boolean, level: "AVAILABLE" | "EXPIRED" | "EXPIRING" | "OUT_OF_STOCK" }) => {
    const { toast } = useToast();
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showUpdateForm, setShowUpdateForm] = useState(false);
    const [newPrice, setNewPrice] = useState(0);
    const [effectiveFrom, setEffectiveFrom] = useState(dayjs().toDate());

    const router = useRouter();
    const [nonMedicalPriceHistory, setNonMedicalPriceHistory] = useState<NonMedicalInventoryPriceHistory[]>([]);

    const [updateValues, setUpdateValues] = useState<{
        discountValue?: number;
        discountType?: DiscountType;
        customBarcode?: string;
        costFromSupplier?: number;
        storageLocation?: string;
        storageRack?: string;
        supplierReturnAllowedDays?: number;
        isReturnable?: boolean;
    }>({
        discountType: DiscountType.NONE,
        discountValue: 0,
        customBarcode: "",
        costFromSupplier: 0,
        storageLocation: "",
        storageRack: "",
        supplierReturnAllowedDays: 0,
        isReturnable: false,
    });


    useEffect(() => {
        async function get() {
            setLoading(true);
            const response = await pharmacyClient.getInventory({
                page: 1,
                pageSize: 1000,
                filter: {
                    itemType: type,
                    inventoryId: itemId,
                    isAvailable: level === "AVAILABLE",
                    isExpired: level === "EXPIRED",
                    expiringSoonDays: level === "EXPIRING" ? 30 : 0,
                    isOutOfStock: level === "OUT_OF_STOCK",
                }
            })
            if (response.items.length === 0) {
                toast({
                    title: "Item not found",
                    variant: "destructive",
                    description: "The item you are trying to edit does not exist.",
                    duration: 5000,
                });
                return;
            }
            setUpdateValues({
                discountType: response.items[0].metadata?.discountType || DiscountType.NONE,
                discountValue: response.items[0].metadata?.discountValue || 0,
                customBarcode: response.items[0].metadata?.customBarcode || "",
                costFromSupplier: response.items[0].costFromSupplier || 0,
                storageLocation: response.items[0].metadata?.storageLocation || "",
                storageRack: response.items[0].metadata?.storageRack || "",
                supplierReturnAllowedDays: response.items[0].metadata?.supplierReturnDaysAllowed || 0,
                isReturnable: response.items[0].metadata?.isReturnable || false
            })
            setLoading(false);
        }
        if (isOpen) {
            get();
        }
    }, [isOpen, itemId, type]);

    return (
        <>
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger>
                    <Button
                        variant="ghost"
                        disabled={disabled}>
                        <FilePenLine className="h-4 w-4" style={{
                            cursor: disabled ? "not-allowed" : "pointer"
                        }} />
                    </Button>
                </SheetTrigger>
                <SheetContent
                    side={"bottom"}
                    className="pb-24 max-h-screen overflow-y-auto"
                    style={{ maxHeight: "100vh" }}
                >
                    <SheetHeader>
                        <SheetTitle>
                            {loading && <Skeleton className="w-1/2 h-6 mb-2"></Skeleton>}
                            {!loading && `Edit ${type === ItemType.MEDICAL ? "Medical" : "Non Medical"} Item`}
                        </SheetTitle>
                        <SheetDescription>
                            {loading && <Skeleton
                                className="w-1/2 h-4 mb-2"
                            ></Skeleton>}
                            {!loading && "Update item details here"}
                        </SheetDescription>
                        <SheetClose
                            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none data-[state=open]:bg-secondary"
                            onClick={() => {
                                setIsOpen(false);
                            }}
                        >
                            <X className="h-4 w-4" />
                            <span className="sr-only">Close</span>
                        </SheetClose>
                    </SheetHeader>

                    <hr className="my-4" />

                    <div className="grid grid-cols-2 space-x-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-sm font-semibold mb-2">Discount</p>
                                <Input type="string" disabled={updateValues.discountType === DiscountType.NONE || loading} placeholder="Enter discount value" value={updateValues.discountValue} onChange={(e) => {
                                    const value = parseFloat(e.target.value);
                                    if (!isNaN(value)) {
                                        setUpdateValues((prev) => ({
                                            ...prev,
                                            discountValue: value
                                        }));
                                    } else {
                                        setUpdateValues((prev) => ({
                                            ...prev,
                                            discountValue: 0
                                        }));
                                    }
                                }}/>
                            </div>

                            <div>
                                <p className="text-sm font-semibold mb-2">Discount Type</p>
                                <Select value={updateValues.discountType?.toString()} disabled={loading} onValueChange={(value) => {
                                    const discountType = DiscountType[value as keyof typeof DiscountType];
                                    setUpdateValues((prev) => ({
                                        ...prev,
                                        discountType: discountType
                                    }));
                                    if (discountType === DiscountType.NONE) {
                                        setUpdateValues((prev) => ({
                                            ...prev,
                                            discountValue: 0
                                        }));
                                    }
                                }}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select discount type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={DiscountType.NONE.toString()}>None</SelectItem>
                                        <SelectItem value={DiscountType.FIXED.toString()}>Fixed</SelectItem>
                                        <SelectItem value={DiscountType.PERCENTAGE.toString()}>Percentage</SelectItem>
                                        <SelectItem value={DiscountType.ALWAYS_FIXED.toString()}>Always Fixed</SelectItem>
                                        <SelectItem value={DiscountType.ALWAYS_PERCENTAGE.toString()}>Always Percentage</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <p className="text-sm font-semibold mb-2">Custom Barcode</p>
                                <Input placeholder="Enter custom barcode" disabled={loading} value={updateValues.customBarcode} onChange={(e) => {
                                    setUpdateValues((prev) => ({
                                        ...prev,
                                        customBarcode: e.target.value
                                    }));                                    
                                }} />
                            </div>
                            <div>
                                <p className="text-sm font-semibold mb-2">Cost of Item</p>
                                <Input placeholder="Enter cost of item" disabled={loading} value={updateValues.costFromSupplier} onChange={(e) => {
                                    const value = parseFloat(e.target.value);
                                    if (!isNaN(value)) {
                                        setUpdateValues((prev) => ({
                                            ...prev,
                                            costFromSupplier: value
                                        }));
                                    } else {
                                        setUpdateValues((prev) => ({
                                            ...prev,
                                            costFromSupplier: 0
                                        }));
                                    }                                    
                                }} />
                            </div>
                            <div>
                                <p className="text-sm font-semibold mb-2">Storage Location</p>
                                <Input placeholder="Enter storage location" disabled={loading} value={updateValues.storageLocation} onChange={(e) => {
                                    setUpdateValues((prev) => ({
                                        ...prev,
                                        storageLocation: e.target.value
                                    }));                                    
                                }} />
                            </div>
                            <div>
                                <p className="text-sm font-semibold mb-2">Storage Rack</p>
                                <Input placeholder="Enter storage rack" disabled={loading} value={updateValues.storageRack} onChange={(e) => {
                                    setUpdateValues((prev) => ({
                                        ...prev,
                                        storageRack: e.target.value
                                    }));                                    
                                }} />
                            </div>
                            <div>
                                <p className="text-sm font-semibold mb-2">Supplier Return Allowed Days</p>
                                <Input placeholder="Enter supplier return days allowed" disabled={loading} value={updateValues.supplierReturnAllowedDays} onChange={(e) => {
                                    const value = parseInt(e.target.value);
                                    if (!isNaN(value)) {
                                        setUpdateValues((prev) => ({
                                            ...prev,
                                            supplierReturnAllowedDays: value
                                        }));
                                    } else {
                                        setUpdateValues((prev) => ({
                                            ...prev,
                                            supplierReturnAllowedDays: 0
                                        }));
                                    }                                    
                                }} />
                            </div>
                            <div>
                                <p className="text-sm font-semibold mb-2">Is Returnable</p>
                                <Select value={updateValues.isReturnable ? "true" : "false"} disabled={loading} onValueChange={(value) => {
                                    setUpdateValues((prev) => ({
                                        ...prev,
                                        isReturnable: value === "true"
                                    }));
                                    if (value === "false") {
                                        setUpdateValues((prev) => ({
                                            ...prev,
                                            supplierReturnAllowedDays: 0
                                        }));
                                    }
                                }}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select returnable option" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="true">Yes</SelectItem>
                                        <SelectItem value="false">No</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        {type === ItemType.NON_MEDICAL && <div>
                            <div className='flex items-center justify-between'>
                                <div>
                                    <p className="font-semibold">Past price updates</p>
                                    <p className="text-gray-500">
                                        Item price updates history
                                    </p>
                                </div>
                                <Button variant={'secondary'} onClick={() => setShowUpdateForm(!showUpdateForm)}>
                                    {showUpdateForm ? 'Hide' : 'Show'} Update Form
                                </Button>
                            </div>
                            {showUpdateForm && <div>
                                <hr className='my-4' />
                                <div className='flex gap-x-4'>
                                    <p className='text-gray-600 text-sm'>New Price</p>
                                    <Input type='number' placeholder='Enter price' value={newPrice} onChange={(e) => {
                                        e.target.value ? setNewPrice(parseFloat(e.target.value)) : setNewPrice(0);
                                    }} />
                                    <Button disabled={loading} onClick={async () => {
                                        try {
                                            setLoading(true);
                                            await pharmacyClient.createNonMedicalInventoryPrice({
                                                inventoryId: itemId,
                                                price: newPrice,
                                                effectiveDate: BigInt(dayjs(effectiveFrom).unix()),
                                            })
                                            toast({
                                                title: "Price updated successfully",
                                                variant: "success",
                                                description: "Price has been updated successfully.",
                                                duration: 5000,
                                            });
                                            setLoading(false);
                                            router.invalidate();
                                        } catch (e) {
                                            console.error(e);
                                            toast({
                                                title: "Failed to update price",
                                                variant: "destructive",
                                                description: "An error occurred while updating the price. Please try again later.",
                                                duration: 5000,
                                            })
                                            setLoading(false);
                                        }
                                    }}>Update Price</Button>
                                </div>
                            </div>}
                            <hr className='my-4' />
                            <div className='flex flex-col space-y-8'>
                                {nonMedicalPriceHistory.map((price, index, array) => (
                                    <div className='flex gap-x-4' key={index}>
                                        <p className='text-gray-600'>{dayjs(price.effective_from).format("DD MMM YYYY")}</p>
                                        <Separator className='mx-4 h-[120px] w-1 bg-gray-500' orientation="vertical" />
                                        <div>
                                            <p className='font-semibold text-gray-700'>{convertToCurrencyRounded(price.price)} {index < array.length - 1 ? `| ${price.price - array[index + 1].price > 0 ? 'Increased By' : 'Decreased By'} ${convertToCurrencyRounded(price.price - array[index + 1].price)}` : null}</p>
                                            {index === 0 && <div className='flex items-center space-x-4 mt-4'>
                                                <p className='text-gray-600 text-sm'>Current Active Price for your Pharmacy <br /> Effective from {dayjs(price.effective_from).format("DD MMM YYYY")}</p>
                                            </div>}
                                            {/* If index is not 0, get the effective from and to dates */}
                                            {index !== 0 && <div className='flex items-center space-x-4 mt-4'>
                                                <p className='text-gray-600 text-sm'>Effective till {dayjs(array[index - 1].effective_from).format("DD MMM YYYY")} from {dayjs(price.effective_from).format("DD MMM YYYY")}</p>
                                            </div>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>}
                    </div>

                    <Button
                        className="absolute bottom-2 right-2"
                        disabled={loading}
                        onClick={async () => {
                            try {
                                if (updateValues.costFromSupplier === 0) {
                                    toast({
                                        title: "Invalid cost",
                                        variant: "warning",
                                        description: "Cost of item cannot be zero.",
                                        duration: 5000,
                                    });
                                    return;
                                }
                                setLoading(true);
                                await pharmacyClient.updateInventoryMetadata({
                                    itemType: type,
                                    inventoryId: itemId,
                                    metadata: {
                                        discountType: updateValues.discountType || DiscountType.NONE,
                                        discountValue: updateValues.discountValue || 0,
                                        customBarcode: updateValues.customBarcode || "",
                                        storageLocation: updateValues.storageLocation || "",
                                        storageRack: updateValues.storageRack || "",
                                        supplierReturnDaysAllowed: updateValues.supplierReturnAllowedDays || 0,
                                        isReturnable: updateValues.isReturnable || false,
                                    }
                                })
                                toast({
                                    title: "Item updated successfully",
                                    variant: "success",
                                    description: "Item has been updated successfully.",
                                    duration: 5000,
                                });
                                setLoading(false);
                                setIsOpen(false);
                                router.invalidate();
                            } catch (e) {
                                console.error(e);
                                toast({
                                    title: "Failed to update item",
                                    variant: "destructive",
                                    description: "An error occurred while updating the item. Please try again later.",
                                    duration: 5000,
                                })
                            }
                        }}
                    >
                        Update Item
                    </Button>
                </SheetContent>
            </Sheet>
        </>
    );
};

export default EditItem;